// The category index (PRD AC3, rev. 1.11): Outlook category names to project codes, kept in the app.
// It replaces the SharePoint Project Categories Index list, which is copied in once. People type and
// rename categories in Outlook; the index says which code each name means. Saving it recounts every
// unresolved entry, so one new row can clear many inbox items at once.

import { codeForCategory, type CodeIndex, type EntryFlag, type TimeEntry } from "./actuals";
import { isValidCode, normalizeCode } from "./projects";

export interface CategoryMapping {
  id: string;
  /** The category name as it appears in Outlook. Matched ignoring case and surrounding spaces. */
  category: string;
  code: string;
  note?: string;
}

export const categoryKey = (category: string) => category.trim().toLowerCase();

/** The code index for counting: known codes, plus the index's names as aliases. */
export function withMappings(known: ReadonlySet<string>, mappings: readonly CategoryMapping[]): CodeIndex {
  return { known, aliases: new Map(mappings.map((m) => [categoryKey(m.category), m.code])) };
}

/** Problems with an index before it is saved. */
export function validateMappings(mappings: readonly Partial<CategoryMapping>[], known: ReadonlySet<string>): string[] {
  const errors: string[] = [];
  const seen = new Map<string, string>();
  for (const m of mappings) {
    const name = typeof m.category === "string" ? m.category.trim() : "";
    if (!name || name.length > 255) errors.push("Every row needs a category name of up to 255 characters.");
    const code = normalizeCode(String(m.code ?? ""));
    if (!isValidCode(code)) errors.push(`"${name}" needs a project code, like LG002.`);
    else if (!known.has(code)) errors.push(`${code} (for "${name}") isn't on the Projects list.`);
    const k = categoryKey(name);
    if (name && seen.has(k) && seen.get(k) !== code) errors.push(`"${name}" is listed twice with different codes.`);
    seen.set(k, code);
    if (m.note !== undefined && (typeof m.note !== "string" || m.note.length > 300)) errors.push(`The note for "${name}" is too long.`);
  }
  return [...new Set(errors)];
}

const CATEGORY_FLAGS: readonly EntryFlag[] = ["uncategorized", "unknown-code", "multi-code", "unpaid-time-off"];

/**
 * An entry recounted against the index: its codes and category flags follow the index; day flags
 * (overlap, long day, Out of office) stay. Resolved entries are left as the admin settled them.
 */
export function recode(e: TimeEntry, index: CodeIndex): TimeEntry {
  if (e.resolution || e.flags.includes("oof-unblocked")) return e;
  // An event added in Outlook after its week froze counts nothing until an admin accepts it (P2b).
  if (e.outlook?.kind === "added" || e.outlookKept?.startsWith("added|")) return e;
  const codes = [...new Set(e.categories.map((c) => codeForCategory(c, index)).filter((c): c is string => !!c))];
  const flags: EntryFlag[] = [];
  if (!e.categories.length) flags.push("uncategorized");
  else if (!codes.length) flags.push("unknown-code");
  if (codes.length > 1) flags.push("multi-code");
  if (codes.includes("3SUTO")) flags.push("unpaid-time-off");
  return { ...e, codes, flags: [...flags, ...e.flags.filter((f) => !CATEGORY_FLAGS.includes(f))] };
}

/** Whether recounting changed an entry. */
export const recoded = (before: TimeEntry, after: TimeEntry) =>
  before.codes.join() !== after.codes.join() || before.flags.join() !== after.flags.join();

/**
 * Rows read from a SharePoint list to mappings, given which column holds the category name and which
 * the code. A code column holding "LG002 - Logic" is read for its leading code. Blank rows are skipped.
 */
export function mappingsFromRows(rows: readonly Record<string, string>[], categoryColumn: string, codeColumn: string, newId: () => string): CategoryMapping[] {
  const out = new Map<string, CategoryMapping>();
  for (const r of rows) {
    const category = (r[categoryColumn] ?? "").trim();
    const raw = (r[codeColumn] ?? "").trim().toUpperCase();
    const code = raw.match(/^([A-Z0-9]{2,12})\b/)?.[1] ?? "";
    if (!category || !code) continue;
    if (!out.has(categoryKey(category))) out.set(categoryKey(category), { id: newId(), category, code });
  }
  return [...out.values()].sort((a, b) => a.category.localeCompare(b.category));
}

/** The leading project code in a list cell ("AN001 · Andela" → AN001), if it is a known one. */
export function leadingCode(text: string, known: ReadonlySet<string>): string | null {
  const code = text.trim().toUpperCase().match(/^([A-Z0-9]{2,12})\b/)?.[1];
  return code && known.has(code) ? code : null;
}

/**
 * Which list columns hold the category name and the code (rev. 1.12), so the copy needs no choices.
 * The code column is the one whose values most often start with a known code. The category column
 * is Title if Title isn't the code column, else the other text column with the most values; when the
 * list has only the one useful column ("AN001 - Andela" in Title), it is both: each row is a category
 * name and its code is the one it starts with.
 */
export function guessListColumns(columns: readonly { name: string; displayName: string }[], rows: readonly Record<string, string>[], known: ReadonlySet<string>): { category: string; code: string } {
  const score = (name: string) => rows.filter((r) => leadingCode(r[name] ?? "", known)).length;
  const filled = (name: string) => rows.filter((r) => (r[name] ?? "").trim()).length;
  const byCode = [...columns].sort((a, b) => score(b.name) - score(a.name) || (a.name === "Title" ? 1 : 0) - (b.name === "Title" ? 1 : 0));
  const code = byCode[0] && score(byCode[0].name) > 0 ? byCode[0].name : "";
  const others = columns.filter((c) => c.name !== code && filled(c.name) > 0);
  const title = others.find((c) => c.name === "Title");
  const category = title?.name ?? [...others].sort((a, b) => filled(b.name) - filled(a.name))[0]?.name ?? code;
  return { category, code };
}

export type SkipReason = "blank" | "no-code" | "unknown-code" | "duplicate";

/** What a copy would add, and why each other row is left out. */
export function planCopy(
  rows: readonly Record<string, string>[],
  columns: { category: string; code: string },
  known: ReadonlySet<string>,
  existing: readonly CategoryMapping[],
  newId: () => string,
): { add: CategoryMapping[]; skipped: { row: number; category: string; value: string; reason: SkipReason }[] } {
  const have = new Set(existing.map((m) => categoryKey(m.category)));
  const add: CategoryMapping[] = [];
  const skipped: { row: number; category: string; value: string; reason: SkipReason }[] = [];
  rows.forEach((r, i) => {
    const category = (r[columns.category] ?? "").trim();
    const value = (r[columns.code] ?? "").trim();
    const lead = value.toUpperCase().match(/^([A-Z0-9]{2,12})\b/)?.[1] ?? "";
    let reason: SkipReason | null = null;
    if (!category) reason = "blank";
    else if (!lead) reason = "no-code";
    else if (!known.has(lead)) reason = "unknown-code";
    else if (have.has(categoryKey(category))) reason = "duplicate";
    if (reason) {
      if (reason !== "blank" || value) skipped.push({ row: i + 1, category, value, reason });
      return;
    }
    have.add(categoryKey(category));
    add.push({ id: newId(), category, code: lead });
  });
  return { add, skipped };
}
